import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Badge, Spinner } from 'react-bootstrap';
import { FaMapMarkerAlt, FaPlus, FaEdit, FaTrash, FaSearch, FaDrawPolygon, FaCircle, FaSquare, FaSync } from 'react-icons/fa';
import { MapContainer, TileLayer, Circle, Polygon, useMap } from 'react-leaflet';
import { GetFromApi, PostToApi, PutToApi, DeleteFromApi } from '../utils/api';
import socketService from '../utils/socket';
import 'leaflet/dist/leaflet.css';
import '../styles/Pages.css';

const Geofence = () => {
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [selectedGeofence, setSelectedGeofence] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geofences, setGeofences] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    type: 'circle',
    trigger_on: 'both',
    color: '#3B82F6',
    radius: 500,
    is_active: true
  });

  // Fetch geofences from API
  const fetchGeofences = async () => {
    try {
      setLoading(true);
      const response = await GetFromApi('/geofences');
      if (response.success) {
        setGeofences(response.data);
      }
    } catch (error) {
      console.error('Error fetching geofences:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGeofences();

    // Subscribe to real-time updates
    socketService.connect();
    socketService.subscribeToGeofences((data) => {
      fetchGeofences();
    });

    return () => {
      socketService.off('geofence:created');
      socketService.off('geofence:updated');
      socketService.off('geofence:deleted');
    };
  }, []);

  const filteredGeofences = geofences.filter(geo => {
    const matchSearch = geo.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchFilter = filterType === 'all' || 
      (filterType === 'active' && geo.is_active) || 
      (filterType === 'inactive' && !geo.is_active);
    return matchSearch && matchFilter;
  });

  const handleOpenModal = (geofence = null) => {
    if (geofence) {
      setEditMode(true);
      setFormData({
        name: geofence.name,
        type: geofence.type,
        trigger_on: geofence.trigger_on,
        color: geofence.color,
        radius: geofence.radius || 500,
        is_active: geofence.is_active
      });
      setSelectedGeofence(geofence);
    } else {
      setEditMode(false);
      setFormData({ name: '', type: 'circle', trigger_on: 'both', color: '#3B82F6', radius: 500, is_active: true });
      setSelectedGeofence(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedGeofence(null);
    setEditMode(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        center: { lat: 23.0225, lng: 72.5714 } // Default center
      };
      
      if (editMode && selectedGeofence) {
        await PutToApi(`/geofences/${selectedGeofence.id}`, data);
      } else {
        await PostToApi('/geofences', data);
      }
      await fetchGeofences();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving geofence:', error);
      alert('Error saving geofence: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this geofence?')) {
      try {
        await DeleteFromApi(`/geofences/${id}`);
        await fetchGeofences();
      } catch (error) {
        console.error('Error deleting geofence:', error);
      }
    }
  };

  const mapCenter = [23.0225, 72.5714];

  return (
    <Container fluid className="p-3">
      <div className="page-header">
        <h4 className="page-title">Geofence Management</h4>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={fetchGeofences} disabled={loading} style={{ borderRadius: '8px' }}>
            {loading ? <Spinner size="sm" /> : <FaSync />}
          </Button>
          <Button variant="primary" onClick={() => handleOpenModal()} style={{ borderRadius: '8px' }}>
            <FaPlus className="me-2" /> Create Geofence
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6' }}><FaDrawPolygon /></div>
              <div><div className="stats-value">{geofences.length}</div><div className="stats-label">Total Geofences</div></div>
            </div>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22C55E' }}><FaCircle /></div>
              <div><div className="stats-value">{geofences.filter(g => g.is_active).length}</div><div className="stats-label">Active</div></div>
            </div>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}><FaCircle /></div>
              <div><div className="stats-value">{geofences.filter(g => !g.is_active).length}</div><div className="stats-label">Inactive</div></div>
            </div>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="stats-card border-0 shadow-sm">
            <div className="d-flex align-items-center">
              <div className="icon-box me-3" style={{ backgroundColor: 'rgba(168, 85, 247, 0.1)', color: '#A855F7' }}><FaMapMarkerAlt /></div>
              <div><div className="stats-value">{geofences.reduce((acc, g) => acc + (g.vehicle_count || 0), 0)}</div><div className="stats-label">Vehicles Inside</div></div>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={5} className="mb-4">
          <Card className="page-card h-100">
            <Card.Header className="bg-white border-bottom">
              <h6 className="mb-0 fw-semibold text-dark">Geofence List</h6>
            </Card.Header>
            <Card.Body>
              <div className="search-input-wrapper mb-3">
                <FaSearch className="search-icon" />
                <Form.Control type="text" placeholder="Search geofences..." value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)} className="form-control-custom" style={{ paddingLeft: '2.5rem' }} />
              </div>
              <div className="filter-tabs mb-3">
                <button className={`filter-tab ${filterType === 'all' ? 'active' : ''}`} onClick={() => setFilterType('all')}>All ({geofences.length})</button>
                <button className={`filter-tab ${filterType === 'active' ? 'active' : ''}`} onClick={() => setFilterType('active')}>Active</button>
                <button className={`filter-tab ${filterType === 'inactive' ? 'active' : ''}`} onClick={() => setFilterType('inactive')}>Inactive</button>
              </div>
              {loading ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {filteredGeofences.map(geo => (
                    <div key={geo.id} className={`geofence-list-item ${selectedGeofence?.id === geo.id ? 'active' : ''}`} onClick={() => setSelectedGeofence(geo)}>
                      <div className="geofence-color-dot" style={{ backgroundColor: geo.color }} />
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-semibold">{geo.name}</span>
                          <Badge bg={geo.is_active ? 'success' : 'secondary'} className="rounded-pill">{geo.is_active ? 'Active' : 'Inactive'}</Badge>
                        </div>
                        <div className="d-flex gap-3 mt-1">
                          <small className="text-muted">{geo.type === 'circle' ? <FaCircle className="me-1" size={10} /> : <FaSquare className="me-1" size={10} />}{geo.type}</small>
                          <small className="text-muted">{geo.vehicle_count || 0} vehicles</small>
                        </div>
                      </div>
                      <div className="ms-2">
                        <button className="action-btn edit me-1" onClick={(e) => { e.stopPropagation(); handleOpenModal(geo); }}><FaEdit size={14} /></button>
                        <button className="action-btn delete" onClick={(e) => { e.stopPropagation(); handleDelete(geo.id); }}><FaTrash size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={7} className="mb-4">
          <Card className="page-card h-100">
            <Card.Header className="bg-white border-bottom"><h6 className="mb-0 fw-semibold text-dark">Geofence Map</h6></Card.Header>
            <Card.Body className="p-0">
              <div style={{ height: '500px' }}>
                <MapContainer center={mapCenter} zoom={12} style={{ height: '100%', width: '100%' }} zoomControl={true}>
                  <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {geofences.filter(g => g.is_active).map(geo => (
                    geo.type === 'circle' && geo.center ? (
                      <Circle key={geo.id} center={[geo.center.lat, geo.center.lng]} radius={geo.radius || 500}
                        pathOptions={{ color: geo.color, fillColor: geo.color, fillOpacity: 0.2 }} />
                    ) : geo.coordinates ? (
                      <Polygon key={geo.id} positions={geo.coordinates.map(c => [c.lat, c.lng])}
                        pathOptions={{ color: geo.color, fillColor: geo.color, fillOpacity: 0.2 }} />
                    ) : null
                  ))}
                </MapContainer>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={handleCloseModal} centered className="modal-custom">
        <Modal.Header closeButton><Modal.Title>{editMode ? 'Edit Geofence' : 'Create Geofence'}</Modal.Title></Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label className="form-label-custom">Geofence Name</Form.Label>
              <Form.Control type="text" className="form-control-custom" placeholder="Enter geofence name"
                value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Type</Form.Label>
                  <Form.Select className="form-control-custom" value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})}>
                    <option value="circle">Circle</option>
                    <option value="polygon">Polygon</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Trigger On</Form.Label>
                  <Form.Select className="form-control-custom" value={formData.trigger_on} onChange={(e) => setFormData({...formData, trigger_on: e.target.value})}>
                    <option value="entry">Entry Only</option>
                    <option value="exit">Exit Only</option>
                    <option value="both">Both</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="form-label-custom">Color</Form.Label>
                  <Form.Control type="color" className="form-control-custom" value={formData.color}
                    onChange={(e) => setFormData({...formData, color: e.target.value})} style={{ height: '42px' }} />
                </Form.Group>
              </Col>
              {formData.type === 'circle' && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-custom">Radius (meters)</Form.Label>
                    <Form.Control type="number" className="form-control-custom" value={formData.radius}
                      onChange={(e) => setFormData({...formData, radius: parseInt(e.target.value)})} min="100" />
                  </Form.Group>
                </Col>
              )}
            </Row>
            <Form.Group className="mb-3">
              <Form.Check type="switch" id="active-switch" label="Active" checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={handleCloseModal}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {editMode ? 'Save Changes' : 'Create Geofence'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Geofence;
